import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { quizAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft, ClipboardCheck, CheckCircle, XCircle,
  ArrowRight, Loader2, Award, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const TalentQuizzes = () => {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    try {
      const res = await quizAPI.talentPending();
      setPending(res.data);
    } catch { toast.error('Failed to load quizzes'); }
    finally { setLoading(false); }
  };

  const startQuiz = async (quizId) => {
    setQuizLoading(true);
    try {
      const res = await quizAPI.get(quizId);
      setActiveQuiz(res.data);
      setQuestions(res.data.questions);
      setCurrentQ(0);
      setAnswers({});
      setResult(null);
    } catch { toast.error('Failed to load quiz'); }
    finally { setQuizLoading(false); }
  };

  const startAssessment = (item) => {
    // GGF certification (and any future virtual assessments) live on
    // their own pages — route there directly instead of loading a quiz.
    if (item.is_ggf || item.start_url) {
      navigate(item.start_url || '/talent/certifications/ggf');
      return;
    }
    startQuiz(item.id);
  };

  const selectAnswer = (questionId, answerIdx) => {
    setAnswers(prev => ({ ...prev, [questionId]: String(answerIdx) }));
  };

  const submitQuiz = async () => {
    const unanswered = questions.filter(q => !(q.id in answers));
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await quizAPI.submit({
        quiz_id: activeQuiz.id,
        answers: Object.entries(answers).map(([qid, ans]) => ({ question_id: qid, selected_answer: ans })),
      });
      setResult(res.data);
      fetchPending(); // Refresh pending list
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuiz = () => {
    setActiveQuiz(null);
    setQuestions([]);
    setAnswers({});
    setResult(null);
    setCurrentQ(0);
  };

  // Quiz Result View
  if (result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="quiz-result-page">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center">
            {result.passed ? (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-green-700 mb-2">Passed!</h2>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-red-600 mb-2">Not Passed</h2>
              </>
            )}
            <p className="text-gray-600 mb-6">{activeQuiz?.title}</p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Your Score</span>
                <span className={`font-bold text-lg ${result.passed ? 'text-green-600' : 'text-red-500'}`} data-testid="quiz-score">{result.percentage}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Required</span>
                <span className="text-gray-900">{result.passing_score}%</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Correct</span>
                <span className="text-gray-900">{result.score} / {result.total_points}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Attempt</span>
                <span className="text-gray-900">#{result.attempt_number}</span>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              {!result.passed && result.can_retake && (
                <Button onClick={() => startQuiz(activeQuiz.id)} variant="outline" data-testid="retake-btn">
                  <RotateCcw className="w-4 h-4 mr-2" />Retake
                </Button>
              )}
              <Button onClick={resetQuiz} className="bg-cyan-500 hover:bg-cyan-600" data-testid="back-to-quizzes-btn">
                Back to Quizzes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz Taking View
  if (activeQuiz && questions.length > 0) {
    const q = questions[currentQ];
    const progress = ((currentQ + 1) / questions.length) * 100;
    const answeredCount = Object.keys(answers).length;

    return (
      <div className="min-h-screen bg-gray-50" data-testid="quiz-taking-page">
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={resetQuiz}><ArrowLeft className="w-4 h-4 mr-2" />Exit Quiz</Button>
            <span className="text-sm font-medium text-gray-700">{activeQuiz.title}</span>
            <span className="text-sm text-gray-400">Q {currentQ + 1}/{questions.length}</span>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-4 py-6">
          <Progress value={progress} className="mb-6 h-2" />

          <Card className="bg-white mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-6">
                <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-bold shrink-0">{currentQ + 1}</span>
                <p className="text-lg text-gray-900 font-medium" data-testid="question-text">{q.question_text}</p>
              </div>

              <div className="space-y-3">
                {(q.options || []).map((opt, oi) => {
                  const isSelected = answers[q.id] === String(oi);
                  return (
                    <button
                      key={oi}
                      onClick={() => selectAnswer(q.id, oi)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-50 text-cyan-900'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid={`answer-option-${oi}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                          isSelected ? 'border-cyan-500 bg-cyan-500 text-white' : 'border-gray-300 text-gray-400'
                        }`}>
                          {q.question_type === 'true_false' ? (oi === 0 ? 'T' : 'F') : String.fromCharCode(65 + oi)}
                        </span>
                        <span className="text-sm">{opt}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentQ(c => Math.max(0, c - 1))}
              disabled={currentQ === 0}
              data-testid="prev-question-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />Previous
            </Button>

            <span className="text-sm text-gray-400">{answeredCount}/{questions.length} answered</span>

            {currentQ < questions.length - 1 ? (
              <Button
                onClick={() => setCurrentQ(c => c + 1)}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="next-question-btn"
              >
                Next<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={submitQuiz}
                disabled={submitting || answeredCount < questions.length}
                className="bg-green-600 hover:bg-green-700"
                data-testid="submit-quiz-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Submit Quiz
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Pending Quizzes List
  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-quizzes-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/talent/dashboard')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <ClipboardCheck className="w-5 h-5 text-cyan-600" />
          <h1 className="text-lg font-semibold text-gray-900">Assessments</h1>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading || quizLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : pending.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">All caught up!</p>
            <p className="text-sm text-gray-400">No pending assessments. You're eligible for all available gigs.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-4">
            {pending.map(q => (
              <Card
                key={q.id}
                className={`bg-white ${q.already_passed ? 'opacity-60' : ''} ${q.is_ggf ? 'border-2 border-amber-400 shadow-md' : ''}`}
                data-testid={q.is_ggf ? 'pending-ggf-card' : 'pending-quiz-card'}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{q.title}</h3>
                        {q.is_ggf && <Badge className="bg-amber-100 text-amber-800 text-[10px]">GGF Compliance</Badge>}
                        {q.is_required && <Badge className="bg-red-100 text-red-700 text-[10px]">Required</Badge>}
                        {q.already_passed && <Badge className="bg-green-100 text-green-700 text-[10px]">Passed</Badge>}
                      </div>
                      {q.description && <p className="text-sm text-gray-500 mb-2">{q.description}</p>}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span>{q.question_count} questions</span>
                        <span>Pass: {q.passing_score}%</span>
                        {q.assigned_role && <span>Role: {q.assigned_role}</span>}
                        {q.assigned_gig && <span>Gig: {q.assigned_gig}</span>}
                        {q.attempt_count > 0 && <span>{q.attempt_count} attempt(s)</span>}
                        {q.time_limit_minutes && <span>{q.time_limit_minutes} min limit</span>}
                      </div>
                    </div>
                    <div>
                      {q.already_passed ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Complete</span>
                        </div>
                      ) : (
                        <Button
                          onClick={() => startAssessment(q)}
                          className={q.is_ggf ? 'bg-amber-500 hover:bg-amber-600' : 'bg-cyan-500 hover:bg-cyan-600'}
                          data-testid={q.is_ggf ? 'start-ggf-assessment-btn' : 'start-quiz-btn'}
                        >
                          {q.is_ggf ? 'Start Assessment' : (q.attempt_count > 0 ? 'Retake' : 'Start')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentQuizzes;
